import { useState, useCallback } from 'react';

import { useRouter } from 'next/compat/router';

import { MinimumWorkspace, APIErrorResponse } from '@repo/api-client';
import {
  sharedConfigStoreSelector,
  useSharedConfigStore,
} from '@repo/editor/src/stores/sharedConfigStore';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import {
  isFreePlan,
  isPaidPlan,
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { Badge } from '@repo/ui/components/Badge';
import { themeStyle } from '@repo/ui/theming';
import IconChevronDown from '@repo/ui-icons/IconChevronDown';
import IconChevronUp from '@repo/ui-icons/IconChevronUp';
import IconPlus from '@repo/ui-icons/IconPlus';
import { Avatar } from '@repo/ui-kit/components/core/Avatars';
import { Button } from '@repo/ui-kit/components/core/Button';
import { Link } from '@repo/ui-kit/components/core/Link';
import { Popover } from '@repo/ui-kit/components/core/Popover';
import { H7, P3 } from '@repo/ui-kit/components/core/Typography';
import { themeStyle as uiThemeStyle } from '@repo/ui-kit/themeStyle';

import { CreateWorkspaceDialog } from 'components/CreateWorkspaceDialog';
import { useSelectWorkspaceUpgrade } from 'components/SelectWorkspaceUpgrade';
import { useWorkspaceInviteDialog } from 'components/WorkspaceInviteDialog/useWorkspaceInviteDialog';
import { FlexColumn } from 'styles/layouts';

import { WorkspacesList, WorkspaceSwitchLabel, Selector } from './styles';
import { useWorkspacesForWorkspaceSelector } from './useWorkspacesForWorkspaceSelector';
import { WorkspaceCurrentSelection } from './WorkspaceCurrentSelection';
import { WorkspaceName } from './WorkspaceName';
import { WorkspaceSelectorItem } from './WorkspaceSelectorItem';

const useFreeWorkspaceLimit = (
  workspaces: MinimumWorkspace[]
): {
  limitReached: boolean;
  limit: number;
  freeWorkspace: MinimumWorkspace | null;
} => {
  const config = useSharedConfigStore(sharedConfigStoreSelector.config);

  const freeWorkspaceLimit = config?.freeWorkspaceLimit;
  if (typeof freeWorkspaceLimit !== 'number') {
    return { limitReached: true, limit: 0, freeWorkspace: null };
  }

  const freeWorkspaces = workspaces.filter(
    (workspace) => !isPaidPlan(workspace.plan)
  );

  return {
    limitReached: freeWorkspaces.length >= freeWorkspaceLimit,
    limit: freeWorkspaceLimit,
    freeWorkspace: freeWorkspaces[0],
  };
};

export const WorkspaceSelector: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const router = useRouter();

  const workspaces = useWorkspacesForWorkspaceSelector();
  const {
    DialogComponent: InviteToWorkspaceDialog,
    dialogProps: inviteDialogProps,
    openDialog: openInviteToWorkspaceDialog,
  } = useWorkspaceInviteDialog();

  const {
    DialogComponent: SelectWorkspaceUpgradeDialog,
    dialogProps: selectWorkspaceUpgradeDialogProps,
    openDialog: openSelectWorkspaceUpgradeDialog,
  } = useSelectWorkspaceUpgrade();

  const [currentWorkspace, workspacesWithoutCurrent] = workspaces ?? [];
  const user = useUserStore(userStoreSelector.user);

  const { limitReached, limit, freeWorkspace } = useFreeWorkspaceLimit(
    (currentWorkspace ? [currentWorkspace] : []).concat(
      workspacesWithoutCurrent ?? []
    )
  );

  const [showWorkspaceCreationModal, setShowWorkspaceCreationModal] =
    useState(false);

  const showModal = useCallback(() => {
    setShowWorkspaceCreationModal(true);
  }, []);

  const handleWorkspaceClick = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleWorkspaceChange = useCallback(
    (workspace: MinimumWorkspace) => {
      router?.push(`/workspace/${workspace.id}/projects`);
    },
    [router]
  );

  const showUpgradeModal = useSubscriptionStore(
    subscriptionStoreSelector.showUpgradeModal
  );

  const handleUpgrade = useCallback(() => {
    // If the current workspace is free, we can show the upgrade modal
    if (isFreePlan(currentWorkspace?.plan)) {
      showUpgradeModal('noIncentiveUpgrade', {
        trackingLabel: 'freeLimitReached',
      });
    }
    // If the current workspace is not free and the limit is greater than 1, we can open the select workspace upgrade dialog
    else if (limit > 1) {
      openSelectWorkspaceUpgradeDialog();
    }
    // If the current workspace is not free, and the limit is 1, we can show the upgrade modal
    else if (freeWorkspace) {
      selectWorkspaceUpgradeDialogProps.setSelectedWorkspaceId(
        freeWorkspace.id
      );
      handleWorkspaceChange(freeWorkspace);
    }
  }, [
    currentWorkspace,
    showUpgradeModal,
    openSelectWorkspaceUpgradeDialog,
    limit,
    handleWorkspaceChange,
    freeWorkspace,
    selectWorkspaceUpgradeDialogProps,
  ]);

  const handleCreateWorkspaceError = useCallback(
    (error: APIErrorResponse<'post', '/workspaces/create'>) => {
      if (error.error === 'FREE_WORKSPACE_LIMIT_REACHED') {
        handleUpgrade();
      }
      // Handle other errors if needed
    },
    [handleUpgrade]
  );

  if (!user || !currentWorkspace || !workspaces) {
    return null;
  }

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger>
          <Selector
            alignItems="center"
            className={open ? 'open' : ''}
            gap="8px"
          >
            <Avatar
              showDotAnnotation
              fallback={currentWorkspace.name}
              round="square"
              variant="photo"
            />
            <WorkspaceName workspace={currentWorkspace} />
            {open ? (
              <IconChevronUp color={themeStyle.varInkMedium} size={12} />
            ) : (
              <IconChevronDown color={themeStyle.varInkMedium} size={12} />
            )}
          </Selector>
        </Popover.Trigger>

        <Popover.Content dataTestId="workspace-selector--content">
          <WorkspaceCurrentSelection currentWorkspace={currentWorkspace} />

          {workspacesWithoutCurrent && workspacesWithoutCurrent?.length > 0 && (
            <WorkspacesList showBorder={workspacesWithoutCurrent?.length > 4}>
              <WorkspaceSwitchLabel>
                <H7 bold color="secondary">
                  Change to
                </H7>
              </WorkspaceSwitchLabel>

              {workspacesWithoutCurrent?.map((workspace) => (
                <WorkspaceSelectorItem
                  key={workspace.id}
                  handleWorkspaceClick={handleWorkspaceClick}
                  workspace={workspace}
                />
              ))}
            </WorkspacesList>
          )}

          <FlexColumn
            gap={uiThemeStyle.spacing2}
            padding={uiThemeStyle.spacing8}
          >
            {limitReached && (
              <FlexColumn style={{ padding: uiThemeStyle.spacing8 }}>
                <P3 color="secondary">
                  <b>You can only have {limit} free team.</b>
                  <br />
                  To create new team,{' '}
                  <Link as="span" color="blue" size="s" onClick={handleUpgrade}>
                    <b>upgrade now.</b>
                  </Link>
                </P3>
              </FlexColumn>
            )}
            <Button
              disabled={limitReached}
              leftIcon={IconPlus}
              name="workspaceCreation"
              size="s"
              variant="ghost"
              width="100%"
              onClick={showModal}
            >
              <Button.Label justifyContent="flex-start">
                Create team
              </Button.Label>
              <Badge
                size="s"
                text="New"
                variant={limitReached ? 'disabled' : 'highlight'}
              />
            </Button>
          </FlexColumn>
        </Popover.Content>
      </Popover.Root>

      <CreateWorkspaceDialog
        isOpen={showWorkspaceCreationModal}
        setIsOpen={setShowWorkspaceCreationModal}
        onCreate={() => {
          openInviteToWorkspaceDialog({ isCreation: true });
        }}
        onError={handleCreateWorkspaceError}
      />
      <InviteToWorkspaceDialog {...inviteDialogProps} />
      <SelectWorkspaceUpgradeDialog
        {...selectWorkspaceUpgradeDialogProps}
        onWorkspaceChange={handleWorkspaceChange}
      />
    </>
  );
};
